/**
 * Pure frequency-cap logic for the site-wide newsletter capture popup. Kept
 * free of React / DOM so it can be unit-tested directly
 * (tests/newsletter-popup-frequency.test.ts).
 *
 * Rules (penalty-safe, non-nagging):
 *   • subscribed  → never show again.
 *   • dismissed   → 7-day cooldown, then eligible again.
 *   • otherwise   → eligible.
 *
 * ONE store governs EVERY page-type variant (blog / PDP / collection / data /
 * recalls / tools / local / financing / general). A single dismiss snoozes all
 * of them for the cooldown window; a single subscribe suppresses all of them
 * forever. The variant only changes the card's shape and copy — never the cap.
 */

import { INTERNAL_SURFACE_PREFIXES } from '@/lib/internal-surfaces';


export const POPUP_STORAGE_KEY = 'fridge_nl_popup_v1';
/**
 * Dismiss cooldown (masterclass cadence, owner-directed): a single dismiss
 * snoozes EVERY variant for 7 days. Kept as `SNOOZE_MS` for import stability.
 */
export const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
export const DISMISS_COOLDOWN_MS = SNOOZE_MS; // explicit alias for readability

/**
 * Shared cross-lane key. The PDP inline Price Watch affordance (owned by a
 * concurrent lane, components/price-watch/*) writes this key on a successful
 * inline subscribe. The popup READS it and never shows the PDP "Track this
 * price?" variant to a user who already used the inline widget — one price
 * intent, one ask. Any truthy value counts as "used".
 */
export const PRICE_WATCH_INLINE_KEY = 'fridge_price_watch_inline_v1';

/** True when the inline PDP Price Watch widget has already been used. */
export function inlineWatchUsed(raw: string | null): boolean {
  return typeof raw === 'string' && raw.trim() !== '';
}

export interface PopupState {
  /** epoch ms of the last dismiss; starts a 30-day snooze. */
  dismissedAt?: number | null;
  /** epoch ms of a successful subscribe; suppresses the popup forever. */
  subscribedAt?: number | null;
}

/** Decide whether the popup is eligible to show given stored state and now. */
export function shouldShowPopup(
  state: PopupState | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!state) return true;
  if (state.subscribedAt) return false; // subscribed = never again
  if (state.dismissedAt && now - state.dismissedAt < SNOOZE_MS) return false; // 7-day cooldown
  return true;
}

/** Safely parse the localStorage blob; corrupt/absent → null (eligible). */
export function parsePopupState(raw: string | null): PopupState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return {
        dismissedAt: typeof parsed.dismissedAt === 'number' ? parsed.dismissedAt : null,
        subscribedAt: typeof parsed.subscribedAt === 'number' ? parsed.subscribedAt : null,
      };
    }
  } catch {
    /* corrupt JSON → treat as no state */
  }
  return null;
}

/** Merge a patch into existing state and serialize for storage. */
export function serializePopupState(prev: PopupState | null, patch: PopupState): string {
  return JSON.stringify({ ...(prev ?? {}), ...patch });
}

/* ────────────────────────────────────────────────────────────────────────
 * Page-type variant selection — ONE system, deterministic on pathname.
 *
 * Every variant funnels into the EXISTING /api/alerts/subscribe machinery:
 *   • PDP        → type 'price_watch' (needs the product UUID, read from the
 *                  on-page DOM at render; falls back to 'general' newsletter
 *                  when no product id is resolvable).
 *   • all others → type 'newsletter', distinguished only by the `source` tag.
 *
 * Sizing hierarchy (owner-directed): PDP largest · blog/data/recalls medium ·
 * collections compact.
 * ──────────────────────────────────────────────────────────────────────── */

export type PopupVariantId =
  | 'pdp'
  | 'blog'
  | 'collection'
  | 'data'
  | 'recalls'
  | 'tools'
  | 'local'
  | 'financing'
  | 'general';

export type PopupVariantSize = 'large' | 'medium' | 'compact';

export type PopupSubscribeType = 'price_watch' | 'newsletter';

export interface PopupVariant {
  id: PopupVariantId;
  size: PopupVariantSize;
  subscribeType: PopupSubscribeType;
  /** newsletter `source` tag; null for price_watch (that type takes no source). */
  source: string | null;
}

/**
 * Pages where the popup is pure noise — never shown.
 *   • /contact + legal — no capture intent there.
 *   • /r — the affiliate redirect path (a checkout-intent moment: the reader
 *     is mid-click to a retailer). It renders no page layout, but excluding it
 *     is a belt-and-braces guarantee the capture never fires on a buy click.
 *   • /embed — the embeddable widgets (FRIDGE-356) are framed inside OTHER
 *     sites' pages; a capture card floating over a 300px-wide widget on a
 *     publisher's page is noise there and would read as the publisher's.
 */
export const EXCLUDED_PREFIXES = [
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
  '/r',
  '/embed',
  // INTERNAL SURFACES — FRIDGE-418. These were missing, so every internal page
  // fell through to `return VARIANTS.general` at the bottom of
  // resolvePopupVariant and an email-capture card rendered over the admin
  // tooling. Seen directly on /operations/analytics and /operations/seo: a
  // "PRICE & RECALL ALERTS — Never overpay for a fridge or freezer" modal
  // covering the operations dashboard.
  //
  // Spread from lib/internal-surfaces.ts rather than retyped, so a new internal
  // namespace cannot be gated in middleware and forgotten here.
  ...INTERNAL_SURFACE_PREFIXES,
];

export function isExcludedPath(pathname: string | null): boolean {
  if (!pathname) return true;
  return EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

const VARIANTS: Record<PopupVariantId, PopupVariant> = {
  pdp: { id: 'pdp', size: 'large', subscribeType: 'price_watch', source: null },
  blog: { id: 'blog', size: 'medium', subscribeType: 'newsletter', source: 'popup-blog' },
  collection: { id: 'collection', size: 'compact', subscribeType: 'newsletter', source: 'popup-collection' },
  data: { id: 'data', size: 'medium', subscribeType: 'newsletter', source: 'popup-data' },
  recalls: { id: 'recalls', size: 'medium', subscribeType: 'newsletter', source: 'popup-recalls' },
  tools: { id: 'tools', size: 'medium', subscribeType: 'newsletter', source: 'popup-tools' },
  local: { id: 'local', size: 'medium', subscribeType: 'newsletter', source: 'popup-local' },
  financing: { id: 'financing', size: 'medium', subscribeType: 'newsletter', source: 'popup-financing' },
  general: { id: 'general', size: 'medium', subscribeType: 'newsletter', source: 'popup' },
};

/** Return the frozen variant descriptor for an id. */
export function popupVariant(id: PopupVariantId): PopupVariant {
  return VARIANTS[id];
}

/**
 * Deterministic route → variant selection. Returns null on excluded pages.
 * A product route ALWAYS resolves to the 'pdp' variant here; the component
 * downgrades to 'general' at render only when it cannot read a product UUID
 * from the DOM (so the price_watch POST can never fire without an id).
 */
export function resolvePopupVariant(pathname: string | null): PopupVariant | null {
  if (isExcludedPath(pathname)) return null;
  const path = pathname as string;
  const seg = (p: string) => path === p || path.startsWith(p + '/');

  if (seg('/products')) return VARIANTS.pdp;
  if (seg('/blogs')) return VARIANTS.blog;
  if (seg('/collections')) return VARIANTS.collection;
  if (seg('/data')) return VARIANTS.data;
  if (seg('/recalls')) return VARIANTS.recalls;
  if (seg('/tools')) return VARIANTS.tools;
  if (seg('/local')) return VARIANTS.local;
  if (seg('/financing')) return VARIANTS.financing;
  return VARIANTS.general;
}

/** The US state a /local/<state> route is about (for the local variant copy).
 *  Slug → Title Case; the component prefers the on-page `.place` label when
 *  present. Returns null on the /local index (no single state). */
export function localStateName(pathname: string | null): string | null {
  if (!pathname) return null;
  const rest = pathname.replace(/^\/local\/?/, '').split('/')[0];
  if (!rest) return null;
  return rest.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The Appliance/Energy index a /data route is about (for the data variant copy). */
export function dataIndexName(pathname: string | null): string {
  if (pathname && (pathname === '/data/energy-index' || pathname.startsWith('/data/energy-index/'))) {
    return 'Energy-Efficiency Index';
  }
  return 'Appliance Price Index';
}

/* ────────────────────────────────────────────────────────────────────────
 * Trigger cadence — the PURE decision, DOM-free and unit-tested.
 *
 * The `usePopupCadence` hook (components/use-popup-cadence.ts) wires real DOM
 * events (the window `load` event, a dwell timer, a scroll listener and — on
 * desktop only — an exit-intent mouseout listener) into the latched signals
 * below and asks this function whether to reveal. Keeping the rule pure is
 * what lets every trigger combination be tested without a browser.
 *
 * FRIDGE-780 (owner 09/16/2026: "do what is best"). The previous cadence
 * revealed on a 5-second floor plus a 15-second guaranteed dwell and a 30%
 * scroll — on the homepage that read as an interstitial that opens on
 * arrival, before the shopper has typed a model number. The card now waits
 * for a REAL signal, whichever comes first:
 *   • 50% page scroll (either device) — the shopper is reading;
 *   • 30 seconds on the page (either device) — the shopper is staying;
 *   • exit intent on desktop only (cursor leaves through the top edge) —
 *     the shopper is leaving; there is no honest touch equivalent.
 * and NEVER before the page is interactive: nothing reveals until the page
 * has hydrated (the hook only arms after mount) AND the window `load` event
 * has fired (`document.readyState === 'complete'`). A signal latched before
 * that point is honoured the moment the page becomes interactive.
 *
 * Nothing here touches the dismissal memory or the frequency cap
 * (shouldShowPopup — 7-day dismiss cooldown, subscribe = never again) or the
 * session cap below; those gates stay exactly as they were.
 * ──────────────────────────────────────────────────────────────────────── */

/** Time on the page before the dwell signal fires (either device). Counted
 *  from arming — after hydration — so it is never earlier than 30 s on page. */
export const DWELL_MS = 30_000;
/** Scroll depth (fraction of the scrollable height) that counts as a real
 *  reading signal. */
export const SCROLL_FRACTION = 0.5;
/** Viewport widths at or below this are treated as mobile (no exit-intent,
 *  compact banner). 767px = below the Tailwind `md` breakpoint. */
export const MOBILE_MAX_WIDTH = 767;

/** True when a viewport width should use the mobile cadence + compact banner. */
export function isMobileViewport(width: number): boolean {
  return width > 0 && width <= MOBILE_MAX_WIDTH;
}

/** Latched signals fed to the cadence decision. */
export interface CadenceSignals {
  /** the page is interactive: hydrated AND the window `load` event has fired */
  interactive: boolean;
  /** the visitor has been on the page for DWELL_MS */
  dwellReached: boolean;
  /** the visitor has scrolled to >= SCROLL_FRACTION of the page */
  scrollReached: boolean;
  /** desktop exit-intent (cursor left the viewport through the top) has fired */
  exitIntent: boolean;
}

/**
 * The reveal rule: never before the page is interactive; then the FIRST of a
 * 50% scroll, 30 seconds on page, or desktop exit intent. Exit intent is
 * ignored on mobile even if somehow set.
 */
export function cadenceShouldReveal(sig: CadenceSignals, isMobile: boolean): boolean {
  if (!sig.interactive) return false; // never before the page is interactive
  if (sig.scrollReached) return true; // 50% scroll — either device
  if (sig.dwellReached) return true; // 30 s on page — either device
  return !isMobile && sig.exitIntent; // desktop exit intent; never on mobile
}

/* ────────────────────────────────────────────────────────────────────────
 * Session cap v2 — up to TWO reveals per visit, never nagging (owner-directed).
 *
 * The site-wide capture is "more consistent across pages": a visitor who
 * engages can see a SECOND popup later in the same visit, but only when it is
 * a genuinely different context — a DIFFERENT page-type variant AND at least a
 * MIN_REVEAL_GAP_MS quiet gap after the first. A hard cap of two then closes the
 * session. This sits ON TOP of (never replaces) the penalty-safe gates:
 *   • the interactive-gated 50% scroll / 30 s dwell / desktop exit-intent
 *     cadence (cadenceShouldReveal),
 *   • the 7-day dismiss cooldown + forever subscribe suppression (shouldShowPopup).
 * All three must pass for a reveal.
 *
 * The tracking is in-memory in the hook (the popup lives in the root layout and
 * never unmounts across client-side navigation, so the count survives a "typical
 * visit"); a hard reload resets it, exactly as the pre-v2 once-per-load cap did.
 * The DECISION is kept pure here so every cap/gap/variant combination is
 * unit-tested headless.
 * ──────────────────────────────────────────────────────────────────────── */

/** Hard ceiling on reveals per visit. */
export const SESSION_POPUP_CAP = 2;
/** Minimum quiet gap between the first and second reveal (3 minutes). */
export const MIN_REVEAL_GAP_MS = 3 * 60 * 1000;

/** Running per-visit reveal tally (in-memory; not persisted). */
export interface SessionRevealState {
  /** how many popups have been revealed this visit so far */
  count: number;
  /** epoch ms of the most recent reveal, or null before any reveal */
  lastRevealAt: number | null;
  /** the page-type variant of the most recent reveal, or null before any */
  lastVariantId: PopupVariantId | null;
}

/** A fresh visit — nothing revealed yet. */
export const EMPTY_SESSION_REVEAL: SessionRevealState = {
  count: 0,
  lastRevealAt: null,
  lastVariantId: null,
};

/** Fold a fresh reveal into the tally. */
export function recordReveal(
  prev: SessionRevealState,
  variantId: PopupVariantId,
  now: number,
): SessionRevealState {
  return { count: prev.count + 1, lastRevealAt: now, lastVariantId: variantId };
}

/**
 * Time-independent pre-gate used when ARMING a page's triggers. Skips only the
 * conditions that can never flip to true within this visit:
 *   • the hard session cap is already reached, or
 *   • this page is the SAME page-type variant as the last reveal (the second
 *     ask must always be a different context).
 * A different-variant page whose min-gap has not yet elapsed still arms — the
 * gap is re-checked at reveal time (canRevealAgain).
 */
export function sessionAllowsArming(
  session: SessionRevealState,
  candidateVariantId: PopupVariantId,
): boolean {
  if (session.count >= SESSION_POPUP_CAP) return false;
  if (session.count > 0 && session.lastVariantId === candidateVariantId) return false;
  return true;
}

/**
 * Full gate evaluated at REVEAL time. The first reveal only needs headroom under
 * the cap; a second reveal additionally needs a different page-type variant AND
 * the min quiet gap since the first.
 */
export function canRevealAgain(
  session: SessionRevealState,
  candidateVariantId: PopupVariantId,
  now: number,
): boolean {
  if (session.count >= SESSION_POPUP_CAP) return false; // hard cap
  if (session.count === 0) return true; // first reveal — cadence + frequency gates decide
  if (session.lastVariantId === candidateVariantId) return false; // must be a different page type
  if (session.lastRevealAt == null) return true;
  return now - session.lastRevealAt >= MIN_REVEAL_GAP_MS; // min quiet gap
}
